// Vista ampliada de una foto del lanzamiento —
// ADR-2026-09-07-lanzamientos-visor-de-foto.
//
// Es a propósito lo más chico que un visor puede ser: la foto en resolución
// completa, y la × para cerrar. Nada más. Acá la foto ya está elegida y el
// trabajo es la ficha; cualquier acción de más sería una segunda pantalla de
// creaciones metida adentro del formulario.
//
// El fondo NO es la foto desenfocada como en creaciones: es la pantalla que
// quedó atrás, borroneada y oscurecida. En creaciones la foto ocupa toda la
// pantalla y el fondo tiene que llenarse con algo; acá el visor sale sobre la
// ficha, y dejarla ver detrás es lo que dice "esto es una mirada de cerca, no
// te fuiste a ningún lado".
//
// Portaleado a `document.body`: el panel de contenido de escritorio es
// `position: fixed` y un overlay montado adentro queda por debajo de la barra
// lateral (project_fixed_panel_stacking_trap).
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';

export default function VisorDeFoto({
  url,
  esVideo = false,
  onCerrar,
  onAnterior,
  onSiguiente,
  posicion,
}: {
  /** URL firmada del archivo en resolución completa. `null` = todavía firmando. */
  url: string | null;
  /**
   * Es un video: se reproduce en vez de dibujarse. Un `<img>` apuntando a un mp4
   * es el ícono roto, que es el mismo modo de falla de las miniaturas sin póster
   * (ADR-2026-09-15-un-video-subido-necesita-su-poster).
   */
  esVideo?: boolean;
  onCerrar: () => void;
  /**
   * Pasar a la foto anterior / siguiente, cuando hay más de una (las fotos de un producto —
   * ADR-2026-10-30-el-visor-de-fotos-pasa-de-una-a-otra). Sin ellas no hay flechas: el visor
   * de una sola foto queda como era.
   */
  onAnterior?: () => void;
  onSiguiente?: () => void;
  /** "2 / 4" debajo de la foto. Sólo tiene sentido con las flechas. */
  posicion?: { actual: number; total: number };
}) {
  const [cargada, setCargada] = useState(false);

  // La foto cambia (se pasó a otra) → vuelve el spinner hasta que baje la nueva.
  useEffect(() => setCargada(false), [url]);

  useEffect(() => {
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
      else if (e.key === 'ArrowLeft') onAnterior?.();
      else if (e.key === 'ArrowRight') onSiguiente?.();
    };
    document.addEventListener('keydown', alaTecla);
    // El fondo no scrollea mientras el visor está abierto: en el teléfono, el
    // scroll del dedo sobre la foto movía la ficha de atrás.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alaTecla);
      document.body.style.overflow = overflow;
    };
  }, [onCerrar, onAnterior, onSiguiente]);

  // En el teléfono se pasa también deslizando: más de 40px de lado, y más de lado que de alto.
  const [toque, setToque] = useState<{ x: number; y: number } | null>(null);

  // Hay flechas si hay más de una foto. Las dos se dibujan siempre (la de la punta, apagada),
  // así la foto no se corre de lugar al llegar al principio o al final.
  const conFlechas = !!onAnterior || !!onSiguiente;
  const anchoMaximo = conFlechas ? 'max-w-[calc(92vw-120px)]' : 'max-w-[92vw]';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={esVideo ? 'Video ampliado' : 'Foto ampliada'}
      onClick={onCerrar}
      onTouchStart={(e) => setToque({ x: e.touches[0].clientX, y: e.touches[0].clientY })}
      onTouchEnd={(e) => {
        if (!toque) return;
        const dx = e.changedTouches[0].clientX - toque.x;
        const dy = e.changedTouches[0].clientY - toque.y;
        setToque(null);
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        if (dx > 0) onAnterior?.();
        else onSiguiente?.();
      }}
      className="animate-visor-entra fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
    >
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar la vista ampliada"
        className="absolute top-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="w-5 h-5" />
      </button>

      {posicion && posicion.total > 1 && (
        <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-[12.5px] font-medium tabular-nums text-white/85">
          {posicion.actual} / {posicion.total}
        </span>
      )}

      {/* La foto con sus flechas PEGADAS a los costados, no en los bordes de la pantalla
          (ADR-2026-10-30-el-visor-de-fotos-pasa-de-una-a-otra): en una pantalla ancha quedaban
          lejos de lo que mueven. Con flechas, la foto se achica lo que ocupan (2 × 44px + aire). */}
      <div className="flex items-center gap-3 sm:gap-4">
        {conFlechas && (
          <Flecha
            etiqueta="Foto anterior"
            onClick={onAnterior}
            icono={<ChevronLeft className="h-6 w-6" />}
          />
        )}

        {!cargada && <Loader2 className="w-6 h-6 animate-spin text-white/70" />}

        {url && esVideo && (
          <video
            src={url}
            controls
            autoPlay
            loop
            playsInline
            onLoadedData={() => setCargada(true)}
            onClick={(e) => e.stopPropagation()}
            className={`max-h-[88vh] ${anchoMaximo} rounded-xl transition-opacity duration-200 ${
              cargada ? 'opacity-100' : 'absolute opacity-0'
            }`}
          />
        )}

        {url && !esVideo && (
          <img
            src={url}
            alt=""
            onLoad={() => setCargada(true)}
            onClick={(e) => e.stopPropagation()}
            // Oculta hasta que bajó entera: mostrarla mientras carga la dibuja por
            // bandas y, con la resolución completa, eso dura bastante.
            className={`max-h-[88vh] ${anchoMaximo} rounded-xl object-contain transition-opacity duration-200 ${
              cargada ? 'opacity-100' : 'absolute opacity-0'
            }`}
          />
        )}

        {conFlechas && (
          <Flecha
            etiqueta="Foto siguiente"
            onClick={onSiguiente}
            icono={<ChevronRight className="h-6 w-6" />}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Una flecha del visor. Sin `onClick` (la foto de la punta) se ve apagada y no hace nada. El
 *  click no llega al fondo: si no, pasar de foto cerraba el visor. Lo frena el `span` y no el
 *  botón: un botón `disabled` no recibe el click, y tocar la flecha apagada cerraba el visor
 *  (review Greptile #231). */
function Flecha({ etiqueta, onClick, icono }: { etiqueta: string; onClick?: () => void; icono: ReactNode }) {
  return (
    <span className="z-10 shrink-0" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        aria-label={etiqueta}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-[background-color,opacity] hover:bg-white/20 disabled:cursor-default disabled:opacity-30 disabled:hover:bg-white/10"
      >
        {icono}
      </button>
    </span>
  );
}
